import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, DEFAULT_CATEGORY_NAME } from '../api/db';
import Sidebar from '../components/layout/Sidebar';
import ArticleSlider from '../components/common/ArticleSlider';
import ShareQR from '../components/common/ShareQR';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import DOMPurify from 'dompurify';
import { getAuthorPhoto, getAuthorInitials } from '../utils/authorPhoto';
import { useAuth } from '../context/AuthContext';

const { FiArrowLeft, FiCalendar, FiEdit2, FiHeart, FiVolume2, FiVolumeX } = FiIcons;

const getArticlePlainText = (article) => {
  if (!article) return '';
  const parts = [article.title || ''];
  (article.content || []).forEach(row => {
    (row.elements || []).forEach(el => {
      if (el.type === 'heading' || el.type === 'paragraph') {
        const tmp = document.createElement('div');
        tmp.innerHTML = el.text || '';
        parts.push(tmp.textContent || '');
      } else if (el.type === 'list') {
        (el.items || []).forEach(item => {
          const tmp = document.createElement('div');
          tmp.innerHTML = item;
          parts.push(tmp.textContent || '');
        });
      }
    });
  });
  return parts.filter(Boolean).join('. ');
};

const getEnglishVoices = () => {
  return window.speechSynthesis.getVoices().filter(v => v.lang.startsWith('en'));
};

const useTTS = () => {
  const [playing, setPlaying] = React.useState(false);
  const [voices, setVoices] = React.useState([]);
  const [selectedVoiceName, setSelectedVoiceName] = React.useState('');
  const pingRef = React.useRef(null);

  React.useEffect(() => {
    const ss = window.speechSynthesis;
    if (!ss) return;
    const load = () => {
      const ev = getEnglishVoices();
      if (ev.length > 0) {
        setVoices(ev);
        setSelectedVoiceName(prev => prev || ev[0].name);
      }
    };
    load();
    ss.addEventListener('voiceschanged', load);
    return () => ss.removeEventListener('voiceschanged', load);
  }, []);

  React.useEffect(() => {
    return () => {
      if (pingRef.current) clearInterval(pingRef.current);
      window.speechSynthesis.cancel();
    };
  }, []);

  const speak = (text) => {
    if (!text || !text.trim()) return;
    const ss = window.speechSynthesis;
    if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
    /* Build utt first, then cancel, then speak — all synchronous.
       This satisfies Android's user-gesture policy while still clearing any previous speech. */
    const allVoices = ss.getVoices();
    const chosen = allVoices.find(v => v.name === selectedVoiceName) || getEnglishVoices()[0] || allVoices[0];
    const utt = new SpeechSynthesisUtterance(text);
    if (chosen) { utt.voice = chosen; utt.lang = chosen.lang; }
    utt.rate = 1;
    utt.onend = () => { if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; } setPlaying(false); };
    utt.onerror = (e) => {
      if (e.error === 'interrupted' || e.error === 'canceled') return;
      console.warn('TTS error', e.error);
      if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
      if (e.error === 'synthesis-failed' || e.error === 'voice-unavailable') {
        /* Cloud voice blocked (e.g. Chrome Android) — retry with no explicit voice */
        const retry = new SpeechSynthesisUtterance(text);
        retry.rate = 1;
        retry.onend = () => setPlaying(false);
        retry.onerror = () => setPlaying(false);
        ss.speak(retry);
        return;
      }
      setPlaying(false);
    };
    ss.cancel();
    ss.speak(utt);
    setPlaying(true);
    pingRef.current = setInterval(() => { if (ss.paused) ss.resume(); }, 5000);
  };

  const pause = () => {
    if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
    window.speechSynthesis.cancel();
    setPlaying(false);
  };

  return { playing, speak, pause, voices, selectedVoiceName, setSelectedVoiceName };
};

const extractYouTubeId = (url) => {
  if (!url) return null;
  // eslint-disable-next-line no-useless-escape
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

const extractCanvaId = (url) => {
  if (!url) return null;
  const match = url.match(/canva\.com\/design\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
};

const extractIframeSrc = (input) => {
  if (!input) return null;
  if (input.trim().startsWith('http://') || input.trim().startsWith('https://')) return input.trim();
  const match = input.match(/<iframe[^>]+src=["']([^"']+)["']/i);
  return match ? match[1] : null;
};

const sanitizeSrc = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.toString() : null;
  } catch {
    return null;
  }
};

const DynamicBlock = ({ element }) => {
  switch (element.type) {
    case 'heading': {
      const Tag = element.level || 'h2';
      const className = Tag === 'h2'
        ? 'text-3xl font-bold text-gray-900 mt-12 mb-6'
        : 'text-2xl font-semibold text-gray-800 mt-8 mb-4';
      return <Tag className={className} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(element.text || '') }} />;
    }

    case 'paragraph': {
      const cleanHtml = DOMPurify.sanitize(element.text || '', { ALLOWED_TAGS: ['br', 'a', 'strong', 'em', 'b', 'i', 'u', 's', 'span'], ALLOWED_ATTR: ['href', 'target', 'rel'] });
      return <p className="text-lg text-gray-600 leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: cleanHtml }} />;
    }

    case 'list': {
      const ListTag = element.ordered ? 'ol' : 'ul';
      const listClass = element.ordered ? 'list-decimal' : 'list-disc';
      return (
        <ListTag className={`${listClass} pl-5 mb-6 space-y-2`}>
          {(element.items || []).map((item, idx) => (
            <li key={idx} className="text-lg text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item) }} />
          ))}
        </ListTag>
      );
    }

    case 'image': {
      if (!element.url) {
        return (
          <div className="my-8 p-8 bg-gray-100 border-2 border-dashed border-gray-300 rounded-xl text-center text-gray-500">
            Image placeholder — add an image URL in the editor
          </div>
        );
      }
      return (
        <figure className="my-8">
          <img src={element.url} alt="Article visual" className="w-full rounded-xl shadow-md" />
        </figure>
      );
    }

    case 'youtube': {
      const videoId = extractYouTubeId(element.url);
      if (!videoId) return <div className="p-4 bg-red-50 text-red-600 rounded">Invalid YouTube URL</div>;
      return (
        <div className="relative w-full pb-[56.25%] h-0 my-8 rounded-xl overflow-hidden shadow-lg">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video player"
            className="absolute top-0 left-0 w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      );
    }

    case 'canva': {
      const canvaId = extractCanvaId(element.url);
      if (!canvaId) return <div className="p-4 bg-red-50 text-red-600 rounded">Invalid Canva URL</div>;
      const embedUrl = `https://www.canva.com/design/${canvaId}/view?embed`;
      return (
        <div className="relative w-full pb-[56.25%] h-0 my-8 rounded-xl overflow-hidden shadow-lg">
          <iframe
            src={embedUrl}
            title="Canva presentation"
            className="absolute top-0 left-0 w-full h-full border-0"
            loading="lazy"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          ></iframe>
        </div>
      );
    }

    case 'iframe': {
      const src = sanitizeSrc(extractIframeSrc(element.url));
      if (!src) return <div className="p-4 bg-red-50 text-red-600 rounded">Invalid embed URL</div>;
      return (
        <div className="relative w-full pb-[56.25%] h-0 my-8 rounded-xl overflow-hidden shadow-lg">
          <iframe
            src={src}
            title="Embedded content"
            className="absolute top-0 left-0 w-full h-full border-0"
            loading="lazy"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          ></iframe>
        </div>
      );
    }

    default:
      return null;
  }
};


const FALLBACK_HERO = '/fallback.png';

const AuthorAvatar = ({ name, size = 'md', onDark = false }) => {
  const photo = getAuthorPhoto(name);
  const initials = getAuthorInitials(name);
  const dims = size === 'sm' ? 'w-8 h-8 text-xs' : size === 'lg' ? 'w-14 h-14 text-base' : 'w-10 h-10 text-sm';
  const ring = onDark ? 'ring-2 ring-white/60' : 'ring-2 ring-gray-200';
  const [imgError, setImgError] = useState(false);

  if (photo && !imgError) {
    return (
      <img
        src={photo}
        alt={name}
        onError={() => setImgError(true)}
        className={`${dims} rounded-full object-cover flex-shrink-0 ${ring}`}
      />
    );
  }
  return (
    <div className={`${dims} rounded-full flex-shrink-0 flex items-center justify-center ${ring}`} style={{ backgroundColor: 'var(--brand-primary)' }}>
      <span className="font-semibold text-white leading-none">{initials}</span>
    </div>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, isSuperuser } = useAuth();
  const [article, setArticle] = useState(null);
  const [articleCategory, setArticleCategory] = useState(null);
  const [heroImage, setHeroImage] = useState(FALLBACK_HERO);
  const [recentArticles, setRecentArticles] = useState([]);
  const [liked, setLiked] = useState(false);
  const { playing: ttsPlaying, speak: ttsSpeak, pause: ttsPause, voices: ttsVoices, selectedVoiceName: ttsVoiceName, setSelectedVoiceName: ttsSetVoice } = useTTS();
  const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  useEffect(() => {
    const load = async () => {
      const found = await db.getArticleBySlug(slug);
      if (found) {
        setArticle(found);
        setArticleCategory(db.getArticleCategory(found));
        setHeroImage(found.cover_image?.trim() || FALLBACK_HERO);
        db.recordView(found.id);
        try {
          const liked_ids = JSON.parse(localStorage.getItem('hl_liked_articles') || '[]');
          setLiked(liked_ids.includes(found.id));
        } catch { /* ignore storage errors */ }
      } else {
        navigate('/blog');
      }
      const all = await db.getArticles(true);
      setRecentArticles(all.filter(a => a.slug !== slug).slice(0, 5));
      window.scrollTo(0, 0);
    };
    load();
  }, [slug, navigate]);

  if (!article) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Image */}
      <div className="w-full h-[40vh] md:h-[60vh] relative">
        <img
          src={heroImage}
          alt={article.title}
          onError={() => setHeroImage(FALLBACK_HERO)}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            <button onClick={() => navigate('/blog')} className="text-white/80 hover:text-white flex items-center gap-2 mb-6 transition-colors">
              <SafeIcon icon={FiArrowLeft} /> Back to Blog
            </button>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 leading-tight max-w-4xl">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-white/80 text-sm">
              <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white font-medium">
                {articleCategory?.name || DEFAULT_CATEGORY_NAME}
              </span>
              <span className="flex items-center gap-2">
                <SafeIcon icon={FiCalendar} />
                {new Date(article.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>
            {article.author_name && (
              <div className="flex items-center justify-between gap-4 mt-2">
                <p className="text-white/80 text-sm">
                  Written by <span className="font-semibold text-white">{article.author_name}</span>
                </p>
                {ttsSupported && (
                  <div className="flex flex-col items-end gap-1.5">
                    {ttsVoices.length > 0 && (
                      <select
                        value={ttsVoiceName}
                        onChange={e => ttsSetVoice(e.target.value)}
                        className="text-xs rounded-lg px-2 py-1 border-0 focus:outline-none cursor-pointer"
                        style={{ backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)', color: '#fff', maxWidth: '180px' }}
                      >
                        {ttsVoices.map(v => (
                          <option key={v.name} value={v.name} style={{ color: '#111', backgroundColor: '#fff' }}>
                            {v.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={() => ttsPlaying ? ttsPause() : ttsSpeak(getArticlePlainText(article))}
                      title={ttsPlaying ? 'Stop reading' : 'Read article aloud'}
                      className="flex items-center gap-2 px-4 py-2 rounded-full border-2 text-sm font-semibold transition-all focus:outline-none flex-shrink-0"
                      style={ttsPlaying
                        ? { backgroundColor: 'rgba(255,255,255,0.95)', borderColor: 'rgba(255,255,255,0.95)', color: 'var(--brand-primary)' }
                        : { backgroundColor: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.7)', color: '#fff', backdropFilter: 'blur(4px)' }
                      }
                    >
                      <SafeIcon icon={ttsPlaying ? FiVolumeX : FiVolume2} className="w-4 h-4" />
                      <span className="hidden sm:inline">{ttsPlaying ? 'Stop' : 'Listen'}</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-16">
          
          {/* Main Content (3/4) */}
          <div className="lg:w-3/4">
            <div className="prose prose-lg max-w-none">
              {article.content && article.content.map((row) => (
                <div 
                  key={row.id} 
                  className={`grid gap-8 ${row.columns === 2 ? 'md:grid-cols-2 items-start' : 'grid-cols-1'}`}
                >
                  {/* Column 0 */}
                  <div>
                    {row.elements.filter(el => el.col === 0).map(el => (
                      <DynamicBlock key={el.id} element={el} />
                    ))}
                  </div>
                  
                  {/* Column 1 (if exists) */}
                  {row.columns === 2 && (
                    <div>
                      {row.elements.filter(el => el.col === 1).map(el => (
                        <DynamicBlock key={el.id} element={el} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {article.author_name && (
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <AuthorAvatar name={article.author_name} size="lg" />
                      <div className="flex items-center gap-3">
                        <p className="text-base font-semibold text-gray-800">Written by {article.author_name}</p>
                        {(isSuperuser || (user?.email && user.email === article.author_email)) && (
                          <button
                            onClick={() => navigate(`/admin/editor/${article.id}`)}
                            title="Edit this article"
                            className="text-gray-400 transition-colors p-1 rounded"
                            onMouseEnter={e=>e.currentTarget.style.color='var(--brand-primary)'}
                            onMouseLeave={e=>e.currentTarget.style.color=''}
                          >
                            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          try {
                            const stored = JSON.parse(localStorage.getItem('hl_liked_articles') || '[]');
                            const next = liked
                              ? stored.filter(id => id !== article.id)
                              : [...stored, article.id];
                            localStorage.setItem('hl_liked_articles', JSON.stringify(next));
                            setLiked(!liked);
                          } catch { /* ignore storage errors */ }
                        }}
                        title={liked ? 'Remove from liked articles' : 'Like this article'}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                          liked
                            ? 'bg-red-50 border-red-200 text-red-500'
                            : 'bg-white border-gray-200 text-gray-400 hover:border-red-200 hover:text-red-400'
                        }`}
                      >
                        <SafeIcon
                          icon={FiHeart}
                          className={`w-4 h-4 transition-transform ${liked ? 'scale-110' : ''}`}
                          style={liked ? { fill: 'currentColor' } : {}}
                        />
                        <span className="text-sm font-medium">{liked ? 'Liked' : 'Like'}</span>
                      </button>
                      <ShareQR
                        url={`${window.location.origin}/#/blog/${article.slug}`}
                        title={article.title}
                        size="lg"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar (1/4) */}
          <div className="lg:w-1/4">
            <div className="sticky top-28">
              <Sidebar />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Slider */}
      {recentArticles.length > 0 && (
        <div className="bg-gray-50 py-16 border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Read Next</h3>
            <ArticleSlider articles={recentArticles} />
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPost;